import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { BillingInfoNavComponent } from '@rma/dashboard/billing/account/account-details';
import { RmaLetModule } from '@rma/dashboard/generic/ui/let';
import { StaticImageUrlPipe } from '@rma/dashboard/generic/util/pipes/static-image-url';
import { PageHeaderComponent } from '@rma/dashboard/layout/public/page-header';
import { provideSyndicationStatistics } from '@rma/dashboard/reporting/public/api-review-syndication-statistics';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { catchError, forkJoin, map, Observable, of, startWith, take } from 'rxjs';
import { MySubscriptionFeatureListComponent } from '../ui-my-subscription-feature-list/my-subscription-feature-list.component';
import { MySubscriptionGoogleCardComponent } from '../ui-my-subscription-google-card/my-subscription-google-card.component';
import { MySubscriptionIntegrationCardComponent } from '../ui-my-subscription-integration-card/my-subscription-integration-card.component';
import { MySubscriptionIsLoadingCardComponent } from '../ui-my-subscription-is-loading-card/my-subscription-is-loading-card.component';
import { MySubscriptionReviewChannelsCardComponent } from '../ui-my-subscription-review-channels-card/my-subscription-review-channels-card.component';
import { MySubscriptionReviewsCardComponent } from '../ui-my-subscription-reviews-card/my-subscription-reviews-card.component';
import { MySubscriptionFacadeService } from './my-subscription.facade.service';

@Component({
  selector: 'rma-my-subscription',
  imports: [
    BillingInfoNavComponent,
    AsyncPipe,
    DatePipe,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MySubscriptionFeatureListComponent,
    MySubscriptionGoogleCardComponent,
    MySubscriptionIntegrationCardComponent,
    MySubscriptionIsLoadingCardComponent,
    MySubscriptionReviewChannelsCardComponent,
    MySubscriptionReviewsCardComponent,
    PageHeaderComponent,
    RmaIconComponent,
    RmaLetModule,
    RouterModule,
    StaticImageUrlPipe,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('subscriptions'), MySubscriptionFacadeService, provideSyndicationStatistics('pastYear')],
  templateUrl: './my-subscription.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MySubscriptionComponent {
  private readonly mySubFacade = inject(MySubscriptionFacadeService);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected readonly rmaTileLoading$: Observable<boolean>;

  protected readonly connectedGoogleAccountOwnerName$ = this.mySubFacade.connectedGoogleAccountOwnerName$;
  protected readonly externalPlatformsWithReviews$ = this.mySubFacade.externalPlatformsWithReviews$;
  protected readonly externalReviewPlatform$ = this.mySubFacade.externalReviewPlatform$;
  protected readonly goodPerformance$ = this.mySubFacade.goodPerformance$;
  protected readonly googleReviewInsights$ = this.mySubFacade.googleReviewInsights$;
  protected readonly platformIcon$ = this.mySubFacade.platformIcon$;
  protected readonly potentialReviews$ = this.mySubFacade.potentialReviews$;
  protected readonly productFeatures$ = this.mySubFacade.productFeatures$;
  protected readonly realtorDomainExposures$ = this.mySubFacade.realtorDomainExposures$;
  protected readonly rmaExposures$ = this.mySubFacade.rmaExposures$;
  protected readonly rmaReviewCount$ = this.mySubFacade.rmaReviewCount$;
  protected readonly showCancel$ = this.mySubFacade.showCancel$;
  protected readonly subscription$ = this.mySubFacade.subscription$;
  protected readonly unsubscribeUrl$ = this.mySubFacade.unsubscribeUrl$;
  protected readonly profile$ = this.mySubFacade.profile$;
  protected readonly imagePath = this.mySubFacade.imagePath;
  protected readonly showPauseSubscription$ = this.mySubFacade.showPauseSubscription$;
  protected readonly hidePageHeader = false;
  protected readonly agentCount$ = this.mySubFacade.agentCount$;

  public constructor() {
    this.hidePageHeader = this.activatedRoute.snapshot.data['hidePageHeader'] ?? false;
    this.rmaTileLoading$ = this.getIsLoading([this.rmaReviewCount$, this.rmaExposures$]);
  }

  private getIsLoading(observables: Observable<object | number | boolean | null>[]): Observable<boolean> {
    const loadingObservables: Observable<object | number | boolean | null>[] = observables.map((x) =>
      x.pipe(
        catchError(() => of(null)),
        take(1),
        nonNullable(),
      ),
    );

    return forkJoin(loadingObservables).pipe(
      map(() => false),
      startWith(true),
    );
  }
}
